import { useMemo, useState } from 'react';
import useFetch from '../hooks/useFetch';
import { post } from '../api/client';
import { useToast } from '../context/ToastContext';
import { Badge, Button, Modal, Field, Input, ConfirmModal } from './UI';

/**
 * How many people someone actually arrived with, against what the uploaded
 * sheet said they would bring.
 *
 * The rule the organisers asked for: more people than the sheet expected is
 * worth a second look, so it goes yellow; the same number or fewer is settled,
 * so it goes green. Nobody is green before they have checked in.
 */
const TONE = { up: 'warning', same: 'success', down: 'success' };

export function partyWords(row) {
  if (!row || row.state === 'pending') return 'Not checked in yet';
  const expected = row.expected ?? 0;
  const actual = row.actual ?? 0;
  const diff = actual - expected;
  const people = (n) => `${n} ${n === 1 ? 'person' : 'people'}`;
  if (diff > 0) return `${people(actual)} with them — ${diff} more than the sheet`;
  if (diff < 0) return `${people(actual)} with them — ${-diff} fewer than the sheet`;
  return actual ? `${people(actual)} with them, as the sheet said` : 'Came alone, as the sheet said';
}

/** The tag itself. `row` is one row of GET /api/attendance/party. */
export function PartyTag({ row, expected = null }) {
  if (!row || row.state === 'pending') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Badge tone="neutral">Not checked in</Badge>
        <span style={{ fontSize: 11, color: 'var(--text-lo)' }}>
          {Number.isFinite(row?.expected ?? expected)
            ? `Sheet says ${(row?.expected ?? expected)} with them`
            : 'Nothing on the sheet'}
        </span>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Badge tone={TONE[row.state] || 'neutral'} dot>
        {row.state === 'up' ? 'More than expected' : row.state === 'down' ? 'Fewer than expected' : 'As expected'}
      </Badge>
      <span style={{ fontSize: 11, color: 'var(--text-lo)' }}>{partyWords(row)}</span>
    </div>
  );
}

/**
 * Party counts for a whole event, keyed by user id. `asking` says whether the
 * event has a published check-in form — without one nobody is ever counted.
 */
export function usePartyCounts(eventId, { active = true } = {}) {
  const { data, loading, reload } = useFetch(
    eventId ? '/attendance/party' : null, { event: eventId }, { skip: !active || !eventId },
  );
  const byUser = useMemo(
    () => Object.fromEntries((data?.rows || []).map((r) => [String(r.user), r])),
    [data],
  );
  return {
    byUser,
    rows: data?.rows || [],
    totals: data?.totals || null,
    asking: Boolean(data?.asking),
    form: data?.form || null,
    loading,
    reload,
  };
}

/**
 * The two things an organiser does to a check-in by hand: mark somebody present
 * who could not scan, and take a check-in off again when it was wrong.
 *
 * Marking asks for the party as well, since that is what the roster compares
 * against the sheet. Removing leaves the person on the event — only the scan
 * goes, and their answers with it, so they can be asked again.
 */
export function CheckInActions({ eventId, person, row, onChange, compact = false }) {
  const toast = useToast();
  const [marking, setMarking] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [companions, setCompanions] = useState('');
  const [reason, setReason] = useState('');

  const here = row && row.state !== 'pending';

  const mark = async () => {
    setBusy(true);
    try {
      const res = await post('/attendance/manual', {
        event: eventId,
        userId: person._id,
        scanType: 'event_entry',
        companions: companions === '' ? undefined : Number(companions),
        reason: reason.trim() || 'Marked present at the desk',
      });
      toast.success(res.message);
      setMarking(false);
      setCompanions(''); setReason('');
      onChange?.();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try {
      const res = await post('/attendance/clear', { event: eventId, user: person._id, withForm: true });
      toast.success(res.message);
      setRemoving(false);
      onChange?.();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  return (
    <>
      <div style={{ display: 'flex', gap: 6, marginTop: compact ? 2 : 6, flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={() => { setCompanions(String(row?.expected ?? 0)); setMarking(true); }}
          style={{ fontSize: 11.5, color: 'var(--gold-300)' }}
        >
          {here ? 'Change' : 'Mark present'}
        </button>
        {here && (
          <button type="button" onClick={() => setRemoving(true)} style={{ fontSize: 11.5, color: 'var(--danger)' }}>
            Remove check-in
          </button>
        )}
      </div>

      <Modal
        open={marking} onClose={() => setMarking(false)} width={420}
        title={here ? 'Change this check-in' : 'Mark present'}
        subtitle={person?.name}
        footer={(
          <>
            <Button variant="subtle" onClick={() => setMarking(false)}>Cancel</Button>
            <Button onClick={mark} loading={busy}>{here ? 'Save' : 'Mark present'}</Button>
          </>
        )}
      >
        <Field
          label="People with them"
          hint={Number.isFinite(row?.expected)
            ? `The sheet says ${row.expected}. This is what the roster compares against.`
            : 'Not counting them. Leave it at 0 if they came alone.'}
        >
          <Input type="number" min="0" value={companions} onChange={(e) => setCompanions(e.target.value)} />
        </Field>
        <Field label="Why by hand?" hint="Kept with the entry so it can be told from a scan">
          <Input value={reason} placeholder="Badge would not scan" onChange={(e) => setReason(e.target.value)} />
        </Field>
      </Modal>

      <ConfirmModal
        open={removing} onClose={() => setRemoving(false)} onConfirm={remove} loading={busy} danger
        title="Remove this check-in?" confirmLabel="Remove the check-in"
        message={`${person?.name} will no longer be marked present, and their check-in answers go too, so they can be asked again. They stay on the event.`}
      />
    </>
  );
}

export default PartyTag;
